import { MontoCRC, MontoUSD } from '@/components/dominio';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAplicacionLineaPropuesta } from '@/hooks/usePropuestasPago';
import type { PropuestaPagoLineaDetalle } from '@adelante/shared';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

interface Props {
  idPropuesta: number;
  linea: PropuestaPagoLineaDetalle | null;
  onClose: () => void;
}

/**
 * Dejar constancia de a qué factura se aplicó realmente el pago de una línea (cuando la factura de la
 * propuesta ya se había cerrado en BC). Solo informativo: no cambia BC ni los montos.
 */
export function AplicacionLineaDialog({ idPropuesta, linea, onClose }: Props) {
  const { anotar, quitar } = useAplicacionLineaPropuesta(idPropuesta);
  const [documento, setDocumento] = useState('');
  const [nota, setNota] = useState('');

  useEffect(() => {
    setDocumento(linea?.aplicacion?.aplicadaADocumento ?? '');
    setNota(linea?.aplicacion?.nota ?? '');
  }, [linea]);

  const dolares = linea?.currencyCode === 'USD';
  const ocupado = anotar.isPending || quitar.isPending;
  const sinDatos = !documento.trim() && !nota.trim();

  function guardar() {
    if (!linea || sinDatos) return;
    anotar.mutate(
      {
        entryNo: linea.entryNo,
        data: { aplicadaADocumento: documento.trim() || null, nota: nota.trim() || null },
      },
      {
        onSuccess: () => {
          toast.success('Anotación guardada');
          onClose();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo guardar'),
      },
    );
  }

  function quitarAnotacion() {
    if (!linea) return;
    quitar.mutate(linea.entryNo, {
      onSuccess: () => {
        toast.success('Anotación quitada');
        onClose();
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo quitar'),
    });
  }

  return (
    <Dialog open={!!linea} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>A qué factura se aplicó este pago</DialogTitle>
          <DialogDescription>
            {linea?.vendorName ?? linea?.vendorCode} — {linea?.documentNo} (
            {linea &&
              (dolares ? (
                <MontoUSD monto={linea.montoAPagar} />
              ) : (
                <MontoCRC monto={linea.montoAPagar} />
              ))}
            ). Úsalo cuando la factura de la propuesta ya estaba cerrada en BC y el pago se aplicó a
            otra. Es solo una anotación: no cambia nada en Business Central.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="aplicada-a">Factura a la que se aplicó en BC</Label>
            <Input
              id="aplicada-a"
              value={documento}
              onChange={(e) => setDocumento(e.target.value)}
              placeholder="Ej. CFR-010694"
              maxLength={30}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="aplicacion-nota">Nota (opcional)</Label>
            <Input
              id="aplicacion-nota"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Ej. CFR-011721 se había pagado con tarjeta"
              maxLength={300}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          {linea?.aplicacion && (
            <Button
              variant="outline"
              className="mr-auto"
              disabled={ocupado}
              onClick={quitarAnotacion}
            >
              Quitar anotación
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button disabled={ocupado || sinDatos} onClick={guardar}>
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
